import React, { useState } from 'react'
import { connect } from 'react-redux'
import { noop } from 'lodash'
import { Locale, Translation } from 'locale'
import { AxeActions } from 'api/AxeAction'
import { isNotNil } from 'utils/predicate'
import { DialogField, DialogRow } from 'view/components/inputs'
import { Groupbox } from 'view/components/panels'
import { DialogWindow } from 'view/components/panels/DialogWindow'
import { FundComboboxField, InstrumentComboboxField } from 'view/shared/entity'
import { createCollateralsFromInstrumentIdGetterSelector } from 'selectors/instrumentSelectors'
import { createStructuredSelector } from 'reselect'

interface AxeAddProps {
  onClose?: () => void
  createAxe: typeof AxeActions.create
  getCollateralsFromInstrumentId: ReturnType<ReturnType<typeof createCollateralsFromInstrumentIdGetterSelector>>
}

const AxeAddComponent: React.FC<AxeAddProps> = ({ onClose = noop, createAxe, getCollateralsFromInstrumentId }) => {
  const [{ initiatorTradingEntityId, instrumentId }, setState] = useState<{
    initiatorTradingEntityId?: string
    instrumentId?: string
  }>({
    initiatorTradingEntityId: undefined,
    instrumentId: undefined
  })

  const handleOnConfirm = () => {
    if (initiatorTradingEntityId && instrumentId) {
      const collaterals = getCollateralsFromInstrumentId(instrumentId)
      createAxe({
        initiatorTradingEntityId,
        collateralBasketId: collaterals.collateralBasket?.id ?? null,
        collateralId: collaterals.collateral?.id ?? null,
        externalAxeId: null,
        updatedValues: {}
      })
      onClose()
    }
  }

  return (
    <DialogWindow
      title={Translation.TC_AXE_NEW}
      confirmButtonEnabled={isNotNil(initiatorTradingEntityId) && isNotNil(instrumentId)}
      onConfirm={handleOnConfirm}
      confirmLabel={Translation.TC_BUTTON_OK}
      onClose={onClose}>
      <Groupbox title={Locale.translate(Translation.SC_AXE_ENTER_DETAILS)}>
        <DialogRow>
          <DialogField>
            <FundComboboxField
              label={Locale.translate(Translation.TC_FUND_LABEL)}
              placeholder={Locale.translate(Translation.SC_FUND_PLACEHOLDER)}
              value={initiatorTradingEntityId}
              instrumentId={instrumentId}
              onUpdate={selectedInitiatorTradingEntityId =>
                setState(s => ({ ...s, initiatorTradingEntityId: selectedInitiatorTradingEntityId }))
              }
              dataTestId={'AxeAddDialog-Fund'}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <InstrumentComboboxField
              label={Locale.translate(Translation.TC_INSTRUMENT_LABEL)}
              placeholder={Locale.translate(Translation.SC_INSTRUMENT_PLACEHOLDER)}
              value={instrumentId}
              fundId={initiatorTradingEntityId}
              onUpdate={selectedInstrumentId => setState(s => ({ ...s, instrumentId: selectedInstrumentId }))}
              dataTestId={'AxeAddDialog-Instrument'}
            />
          </DialogField>
        </DialogRow>
      </Groupbox>
    </DialogWindow>
  )
}

export const AxeAdd = connect(
  createStructuredSelector({
    getCollateralsFromInstrumentId: createCollateralsFromInstrumentIdGetterSelector()
  }),
  {
    createAxe: AxeActions.create
  }
)(AxeAddComponent)
